/**
 * Watchlist share bottom sheet presentation.
 *
 * Composed on the shared MobileSheetChrome primitive (SE-5547), which owns
 * the sheet surface, ✕ close chip, height cap, and every dismissal path
 * (backdrop, swipe-down, ESC, Android back). The bespoke snapPoints /
 * 80%-window-height style / w-1/4 desktop width this view used to carry are
 * gone — the chrome owns the surface. The watchlist name renders as the
 * chrome title; the body keeps the swipeable FlashList of users.
 * @module components/organisms/Share
 */

import { ReactElement, memo } from "react";

import { FlashList } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";
import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";

import type { ListRenderItem } from "@/components/atoms/FlashList";
import {
  WatchlistUserFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { SwipeableProvider } from "@/providers/Swipeable";

/**
 * Props for ShareView component
 */
interface Props {
  onClose: () => void;
  isOpen: boolean;
  users: WatchlistUserFragment[];
  watchlist: WatchlistWatchlistMetaFragment;
  refreshing: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderShareActionItem: ListRenderItem<WatchlistUserFragment>;
  renderListFooterComponent: () => ReactElement | null;
}
const keyExtractor = (user: WatchlistUserFragment) => user.id;

/**
 * Pure view component for the watchlist sharing bottom sheet.
 * Displays the list of users to share the watchlist with; the chrome owns
 * the title (watchlist name) and every dismissal path.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the sheet is open
 * @param root0.onClose - Handler for sheet close
 * @param root0.users - Array of users to display
 * @param root0.watchlist - Watchlist being shared
 * @param root0.refreshing - Whether list is refreshing
 * @param root0.onLoadMore - Handler for loading more users
 * @param root0.onRefresh - Handler for pull to refresh
 * @param root0.renderShareActionItem - Function to render each user item
 * @param root0.renderListFooterComponent - Function to render list footer
 */
const ShareView = ({
  isOpen,
  onClose,
  users,
  watchlist,
  refreshing,
  onLoadMore,
  onRefresh,
  renderShareActionItem,
  renderListFooterComponent,
}: Props) => (
  <MobileSheetChrome isOpen={isOpen} onClose={onClose} title={watchlist.name}>
    <SwipeableProvider>
      <FlashList
        data={users}
        keyExtractor={keyExtractor}
        contentContainerClassName="grow"
        refreshing={refreshing}
        showsVerticalScrollIndicator={false}
        renderItem={renderShareActionItem}
        ListFooterComponent={renderListFooterComponent}
        onEndReached={onLoadMore}
        onRefresh={onRefresh}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["white"]}
            tintColor="white"
          />
        }
      />
    </SwipeableProvider>
  </MobileSheetChrome>
);

ShareView.displayName = "ShareView";

export default memo(ShareView);
